import React, { useEffect, useState, useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Stack,
  Grid,
  IconButton,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Menu,
  MenuItem,
  Collapse,
  Snackbar,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Select,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { request } from '../utils/request';
import { toast } from '../utils/toast';
import { ColorModeContext } from '../index';
import ReusableHeader from '../components/Header/ReusableHeader';
import TableFilterBar from '../components/Table/TableFilterBar';
import '../../src/styles/global.css';
import { hasFeatureAccess, getStoredUser, FEATURES } from '../utils/rbac';
import AccessDenied from '../components/guards/AccessDenied';

/* =========================================================
   mEDGE Design Tokens
   ========================================================= */
const LIGHT_VARS = {
  '--vault-navy': '#0B2545',
  '--edge-blue': '#1F6FEB',
  '--canvas': '#FFFFFF',
  '--mist': '#F7F9FC',
  '--hairline': '#E4E9F0',
  '--blue-tint': '#EAF2FE',
  '--slate': '#6B7A90',
  '--secure': '#00A88F',
  '--high': '#E2622B',
  '--elevated': '#F0A202',
  '--critical': '#D6323A',
  '--border-radius': '6px',
};

const DARK_VARS = {
  '--vault-navy': '#F4F7FB',
  '--edge-blue': '#4C9AFF',
  '--canvas': '#0F1729',
  '--mist': '#252b3b',
  '--hairline': 'rgba(255,255,255,0.14)',
  '--blue-tint': 'rgba(76,154,255,0.16)',
  '--slate': '#93A4BD',
  '--secure': '#2FD3B8',
  '--high': '#F07C46',
  '--elevated': '#FFC24B',
  '--critical': '#FF6B72',
  '--border-radius': '6px',
};

const AVATAR_PALETTES = [
  { bg: '#DCE8FD', color: '#1F6FEB' },
  { bg: '#EFE0FB', color: '#8A3FFC' },
  { bg: '#D8F5E9', color: '#00A88F' },
  { bg: '#FEE8D6', color: '#E2622B' },
  { bg: '#FDE2E4', color: '#E53E3E' },
  { bg: '#E2F0D9', color: '#38A169' },
];

/* =========================================================
   Default / Baseline Hierarchy as per PERMISSIONS_CATALOG.md
   4 Parent Modules & 9 Children / Features (36 permissions)
   ========================================================= */
const BASELINE_ORGANIZATION_OPTIONS = [
  {
    id: 'dashboard',
    Label: 'MODULES',
    menu: [
      { id: 'Dashboard', Label: 'Dashboard' },
    ],
  },
  {
    id: 'MFA Control',
    Label: 'MFA CONTROL',
    menu: [
      { id: 'Devices', Label: 'Devices' },
      { id: 'Windows Agent Devices', Label: 'Windows Agent Devices' },
      { id: 'AD Integration', Label: 'AD Integration' },
      { id: 'Applications', Label: 'Applications' },
    ],
  },
  {
    id: 'User Settings',
    Label: 'USER SETTINGS',
    menu: [
      { id: 'Role Master', Label: 'Role Master' },
      { id: 'User Management', Label: 'User Management' },
      { id: 'Permission Master', Label: 'Permission Master' },
    ],
  },
  {
    id: 'SCIM Integrations',
    Label: 'SCIM INTEGRATIONS',
    menu: [
      { id: 'Connections', Label: 'Connections' },
    ],
  },
];

// System tokens always preserved/injected as documented in Section 4 of PERMISSIONS_CATALOG.md
const ALWAYS_INJECTED_SYSTEM_TOKENS = ['Exit', 'Submit', 'Cancel'];

/* =========================================================
   Helper: Build Granular Permission Catalog from Organization Options
   Each child has 4 actions: Read (View), Write (Create), Update (Edit), Print (Delete)
   String Format: `${parent}-${child}-${child}${action}`
   ========================================================= */
const buildPermissionCatalog = (options) => {
  const groups = [];

  options.forEach((parent) => {
    const parentId = parent.id;
    const parentLabel = parent.Label || parentId;
    const menuItems = parent.menu || [];

    if (menuItems.length === 0) return;

    // Determine if group has 1 feature or multiple features
    const isSingleFeature = menuItems.length === 1;

    const buildFeaturePermissions = (child) => {
      const childId = child.id;
      const childLabel = child.Label || childId;

      const readPermKey = `${parentId}-${childId}-${childId}Read`;
      const writePermKey = `${parentId}-${childId}-${childId}Write`;
      const updatePermKey = `${parentId}-${childId}-${childId}Update`;
      const printPermKey = `${parentId}-${childId}-${childId}Print`;

      return [
        {
          id: `${parentId}_${childId}_Read`,
          actionType: 'Read',
          label: `View ${childLabel}`,
          dependsOn: null,
          primaryKey: readPermKey,
          aliases: [readPermKey, `${childId}Read`, `${childId}`, `View ${childLabel}`],
          feature: childId,
          parent: parentId,
        },
        {
          id: `${parentId}_${childId}_Write`,
          actionType: 'Write',
          label: `Create ${childLabel}`,
          dependsOn: `${parentId}_${childId}_Read`,
          primaryKey: writePermKey,
          aliases: [
            writePermKey,
            `${parentId}-${childId}-${childId}Create`,
            `${childId}Write`,
            `${childId}Create`,
            `Create ${childLabel}`,
          ],
          feature: childId,
          parent: parentId,
        },
        {
          id: `${parentId}_${childId}_Update`,
          actionType: 'Update',
          label: `Edit ${childLabel}`,
          dependsOn: `${parentId}_${childId}_Read`,
          primaryKey: updatePermKey,
          aliases: [
            updatePermKey,
            `${parentId}-${childId}-${childId}Edit`,
            `${childId}Update`,
            `${childId}Edit`,
            `Edit ${childLabel}`,
          ],
          feature: childId,
          parent: parentId,
        },
        {
          id: `${parentId}_${childId}_Print`,
          actionType: 'Print',
          label: `Delete ${childLabel}`,
          dependsOn: `${parentId}_${childId}_Read`,
          primaryKey: printPermKey,
          aliases: [
            printPermKey,
            `${parentId}-${childId}-${childId}Delete`,
            `${childId}Print`,
            `${childId}Delete`,
            `Delete ${childLabel}`,
          ],
          feature: childId,
          parent: parentId,
        },
      ];
    };

    if (isSingleFeature) {
      const child = menuItems[0];
      groups.push({
        id: parentId,
        title: parentLabel,
        isSingleFeature: true,
        featureTitle: child.Label || child.id,
        permissions: buildFeaturePermissions(child),
      });
    } else {
      const subsections = menuItems.map((child) => ({
        id: `${parentId}_${child.id}`,
        title: child.Label || child.id,
        permissions: buildFeaturePermissions(child),
      }));

      groups.push({
        id: parentId,
        title: parentLabel,
        isSingleFeature: false,
        subsections,
      });
    }
  });

  return groups;
};

// Flatten helper to extract all permission items
const extractAllPermissions = (groups) => {
  const list = [];
  groups.forEach((g) => {
    if (g.permissions) {
      list.push(...g.permissions);
    } else if (g.subsections) {
      g.subsections.forEach((s) => {
        list.push(...s.permissions);
      });
    }
  });
  return list;
};

// Check if a stored role's permission matches a catalog item
const isRolePermissionMatched = (permItem, rolePermissions) => {
  if (!Array.isArray(rolePermissions) || rolePermissions.length === 0) return false;
  const set = new Set(rolePermissions.map((s) => String(s).toLowerCase().trim()));
  if (set.has(permItem.id.toLowerCase())) return true;
  if (set.has(permItem.primaryKey.toLowerCase())) return true;
  return permItem.aliases.some((a) => set.has(a.toLowerCase()));
};

/* =========================================================
   Main Component: Role
   ========================================================= */
export const Role = () => {
  const user = getStoredUser();
  const canRead = hasFeatureAccess(user, FEATURES.ROLE_MASTER, 'read');
  const canCreate = hasFeatureAccess(user, FEATURES.ROLE_MASTER, 'create');
  const canUpdate = hasFeatureAccess(user, FEATURES.ROLE_MASTER, 'update');
  const canDelete = hasFeatureAccess(user, FEATURES.ROLE_MASTER, 'delete');

  const { mode } = useContext(ColorModeContext || { mode: 'light' });
  const V = mode === 'dark' ? DARK_VARS : LIGHT_VARS;

  // Retrieve organization hierarchy from Redux store
  const organizationState = useSelector((state) => state.organization);

  const organizationOptions = useMemo(() => {
    if (Array.isArray(organizationState) && organizationState.length > 0) {
      const hospitalOrg = organizationState.find((item) => item.id === 'hospital');
      if (hospitalOrg && Array.isArray(hospitalOrg.options) && hospitalOrg.options.length > 0) {
        return hospitalOrg.options;
      }
    }
    return BASELINE_ORGANIZATION_OPTIONS;
  }, [organizationState]);

  // Dynamically built groups and flat permission list
  const permissionGroups = useMemo(() => {
    return buildPermissionCatalog(organizationOptions);
  }, [organizationOptions]);

  const allCatalogPermissions = useMemo(() => {
    return extractAllPermissions(permissionGroups);
  }, [permissionGroups]);

  const totalCatalogPermissionsCount = allCatalogPermissions.length;

  // State
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Row selection
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Modal dialog (Create / Edit)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState('new'); // 'new' | 'edit'
  const [editingRecord, setEditingRecord] = useState(null);

  // Form fields
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [roleStatus, setRoleStatus] = useState('Active');
  const [selectedPermIds, setSelectedPermIds] = useState(new Set());
  const [datePermissionChecked, setDatePermissionChecked] = useState(false);
  const [roleError, setRoleError] = useState('');

  // Group expand/collapse state
  const [expandedGroups, setExpandedGroups] = useState({});

  // Initialize all groups to expanded whenever groups change
  useEffect(() => {
    const initial = {};
    permissionGroups.forEach((g) => {
      initial[g.id] = true;
    });
    setExpandedGroups(initial);
  }, [permissionGroups]);

  // Action Menu (3 Dots)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRecord, setMenuRecord] = useState(null);

  // Delete Dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingRole, setDeletingRole] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Feedback Snackbar
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  const showSnack = (message, severity = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  /* =========================================================
     Fetch Roles and Users
     ========================================================= */
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await request('/role-master/list', { method: 'GET' });
      if (res?.success) {
        setRoles(res.data || []);
      } else {
        console.warn('fetchRoles warning:', res);
        toast('Error fetching roles', { variant: 'error' });
      }
    } catch (err) {
      console.error('fetchRoles error:', err);
      toast('Error fetching roles', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await request('user', { method: 'get' });
      const rawUsers = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setUsers(rawUsers);
    } catch (err) {
      console.warn('fetchUsers fallback:', err);
      setUsers([]);
    }
  };

  useEffect(() => {
    if (!canRead) return;
    let mounted = true;
    (async () => {
      if (!mounted) return;
      await Promise.all([fetchRoles(), fetchUsers()]);
    })();
    return () => {
      mounted = false;
    };
  }, [canRead]);

  // Map users by roleId for avatar listing
  const usersByRoleId = useMemo(() => {
    const map = new Map();
    users.forEach((u) => {
      if (u && u.roleId) {
        const key = String(u.roleId);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(u);
      }
    });
    return map;
  }, [users]);

  // Compute matched assigned permissions count for any given role
  const countAssignedPermissions = useMemo(() => {
    return (role) => {
      if (!role || !Array.isArray(role.permissions)) return 0;
      let matchedCount = 0;
      allCatalogPermissions.forEach((p) => {
        if (isRolePermissionMatched(p, role.permissions)) {
          matchedCount += 1;
        }
      });
      if (matchedCount > 0) return matchedCount;
      const nonSystem = role.permissions.filter(
        (t) => !ALWAYS_INJECTED_SYSTEM_TOKENS.includes(t) && t !== 'DatePermission'
      );
      return nonSystem.length;
    };
  }, [allCatalogPermissions]);

  // Search filter
  const filteredRoles = useMemo(() => {
    if (!searchQuery) return roles;
    const lower = searchQuery.toLowerCase();
    return roles.filter(
      (r) =>
        (r.role || '').toLowerCase().includes(lower) ||
        (r.description || '').toLowerCase().includes(lower)
    );
  }, [roles, searchQuery]);

  // Pagination slice
  const paginatedRoles = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRoles.slice(start, start + rowsPerPage);
  }, [filteredRoles, page, rowsPerPage]);

  /* =========================================================
     Row Selection
     ========================================================= */
  const handleSelectAllRows = (e) => {
    if (e.target.checked) {
      setSelectedRowIds(new Set(filteredRoles.map((r) => r._id)));
    } else {
      setSelectedRowIds(new Set());
    }
  };

  const handleToggleRow = (id) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* =========================================================
     3-Dots Action Menu
     ========================================================= */
  const handleOpenMenu = (event, record) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuRecord(record);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRecord(null);
  };

  /* =========================================================
     Dialog Open / Close
     ========================================================= */
  const openDialog = (action = 'new', record = null) => {
    setCurrentAction(action);
    setEditingRecord(record);
    setRoleError('');

    if (action === 'edit' && record) {
      setRoleName(record.role || '');
      setRoleDescription(record.description || '');
      setRoleStatus(record.status || 'Active');

      // Populate selected permissions based on exact catalog matching
      const matched = new Set();
      allCatalogPermissions.forEach((p) => {
        if (isRolePermissionMatched(p, record.permissions)) {
          matched.add(p.id);
        }
      });
      setSelectedPermIds(matched);

      // Check if DatePermission token is present
      const hasDate = Array.isArray(record.permissions) && record.permissions.includes('DatePermission');
      setDatePermissionChecked(hasDate);
    } else {
      setRoleName('');
      setRoleDescription('');
      setRoleStatus('Active');
      setSelectedPermIds(new Set());
      setDatePermissionChecked(false);
    }

    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setCurrentAction('new');
    setEditingRecord(null);
    setRoleName('');
    setRoleDescription('');
    setRoleStatus('Active');
    setSelectedPermIds(new Set());
    setDatePermissionChecked(false);
    setRoleError('');
  };

  /* =========================================================
     Permission Toggle Logic
     - Toggling an unchecked permission checks it (auto-selecting View if dependent).
     - Unticking a permission ONLY unticks that specific permission.
       It never unchecks or alters other permissions in the module.
     ========================================================= */
  const handlePermToggle = (item) => {
    setSelectedPermIds((prev) => {
      const next = new Set(prev);
      const isCurrentlyChecked = next.has(item.id);

      if (isCurrentlyChecked) {
        // Uncheck ONLY the clicked permission
        next.delete(item.id);
      } else {
        // Check
        next.add(item.id);

        // If checking an action that depends on View, automatically check View
        if (item.dependsOn) {
          next.add(item.dependsOn);
        }
      }
      return next;
    });
  };

  const getGroupPermissionsList = (group) => {
    if (group.permissions) return group.permissions;
    if (group.subsections) return group.subsections.flatMap((s) => s.permissions);
    return [];
  };

  const getGroupSelectedCount = (group) => {
    const list = getGroupPermissionsList(group);
    return list.filter((p) => selectedPermIds.has(p.id)).length;
  };

  const getGroupTotalCount = (group) => {
    return getGroupPermissionsList(group).length;
  };

  const toggleSelectAllGroup = (group) => {
    const list = getGroupPermissionsList(group);
    const selectedCount = getGroupSelectedCount(group);
    const isAllSelected = selectedCount === list.length;

    setSelectedPermIds((prev) => {
      const next = new Set(prev);
      if (isAllSelected) {
        list.forEach((p) => next.delete(p.id));
      } else {
        list.forEach((p) => next.add(p.id));
      }
      return next;
    });
  };

  const toggleGroupExpand = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  /* =========================================================
     Save Role (Create / Update)
     Generates exact `${parent}-${child}-${child}${action}` keys
     Appends system tokens: Exit, Submit, Cancel, DatePermission
     ========================================================= */
  const onSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const trimmed = roleName.trim();
    if (!trimmed) {
      setRoleError('Role name is required.');
      return;
    }

    setSaving(true);
    try {
      const isEdit = currentAction === 'edit';

      // 1. Gather exact primary permission strings for selected items
      const permissionStrings = new Set();
      allCatalogPermissions.forEach((p) => {
        if (selectedPermIds.has(p.id)) {
          permissionStrings.add(p.primaryKey);
          // Also save readable label so both token styles work seamlessly
          permissionStrings.add(p.label);
        }
      });

      // 2. Always inject default system tokens as per catalog
      ALWAYS_INJECTED_SYSTEM_TOKENS.forEach((t) => permissionStrings.add(t));

      // 3. Inject DatePermission if enabled
      if (datePermissionChecked) {
        permissionStrings.add('DatePermission');
      }

      // 4. Preserve any extra legacy tokens already attached to the role
      if (isEdit && editingRecord && Array.isArray(editingRecord.permissions)) {
        editingRecord.permissions.forEach((token) => {
          const isCovered = allCatalogPermissions.some((p) =>
            p.aliases.some((a) => a.toLowerCase() === String(token).toLowerCase())
          );
          if (!isCovered && !ALWAYS_INJECTED_SYSTEM_TOKENS.includes(token) && token !== 'DatePermission') {
            permissionStrings.add(token);
          }
        });
      }

      const finalPermissions = Array.from(permissionStrings);

      const payload = {
        role: trimmed,
        permissions: finalPermissions,
        description: roleDescription,
        status: roleStatus,
      };

      const endpoint = isEdit ? '/role-master/update' : '/role-master/create';
      const method = isEdit ? 'PUT' : 'POST';

      await request(endpoint, {
        method,
        params: isEdit ? editingRecord._id : null,
        body: payload,
      });

      toast(`Role ${isEdit ? 'updated' : 'created'} successfully`, { variant: 'success' });
      showSnack(`Role "${trimmed}" ${isEdit ? 'updated' : 'created'} successfully.`, 'success');
      await fetchRoles();
      closeDialog();
    } catch (err) {
      console.error('onSubmit error:', err);
      toast('Error saving role', { variant: 'error' });
      showSnack('Error saving role', 'error');
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     Delete Handlers
     ========================================================= */
  const handleDeleteClick = (record) => {
    setDeletingRole(record);
    setDeleteOpen(true);
    handleCloseMenu();
  };

  const handleCancelDelete = () => {
    setDeletingRole(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingRole || !deletingRole._id) return;
    setDeleting(true);
    try {
      await request('/role-master/delete', { method: 'DELETE', params: deletingRole._id });
      toast('Role deleted', { variant: 'success' });
      showSnack(`Role "${deletingRole.role}" successfully deleted.`, 'success');
      await fetchRoles();
      handleCancelDelete();
    } catch (err) {
      console.error('onDelete error:', err);
      toast('Error deleting role', { variant: 'error' });
      showSnack('Error deleting role', 'error');
      setDeleting(false);
    }
  };

  /* =========================================================
     Render Group Permissions in Accordion Card
     ========================================================= */
  const renderGroupContent = (group) => {
    if (group.isSingleFeature && group.permissions) {
      return (
        <Grid container spacing={1}>
          {group.permissions.map((perm) => (
            <Grid item xs={12} sm={6} key={perm.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selectedPermIds.has(perm.id)}
                    onChange={() => handlePermToggle(perm)}
                    sx={{
                      color: 'var(--hairline)',
                      '&.Mui-checked': { color: 'var(--edge-blue)' },
                      p: 0.5,
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.825rem',
                      color: 'var(--vault-navy)',
                      userSelect: 'none',
                    }}
                  >
                    {perm.label}
                  </Typography>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (group.subsections) {
      return group.subsections.map((sub, idx) => (
        <Box key={sub.id} sx={{ mb: idx < group.subsections.length - 1 ? 2 : 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.85rem',
              color: 'var(--vault-navy)',
              mb: 1,
            }}
          >
            {sub.title}
          </Typography>
          <Grid container spacing={1}>
            {sub.permissions.map((perm) => (
              <Grid item xs={12} sm={6} key={perm.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedPermIds.has(perm.id)}
                      onChange={() => handlePermToggle(perm)}
                      sx={{
                        color: 'var(--hairline)',
                        '&.Mui-checked': { color: 'var(--edge-blue)' },
                        p: 0.5,
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.825rem',
                        color: 'var(--vault-navy)',
                        userSelect: 'none',
                      }}
                    >
                      {perm.label}
                    </Typography>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ));
    }

    return null;
  };

  /* =========================================================
     Pagination Helpers
     ========================================================= */
  const totalRoles = filteredRoles.length;
  const totalPages = Math.ceil(totalRoles / rowsPerPage) || 1;
  const startItem = totalRoles === 0 ? 0 : page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, totalRoles);

  if (!canRead) {
    return <AccessDenied featureName="Role Master" title="Role Master" />;
  }

  return (
    <Box p={2} style={V}>
      {/* Header with Title and Add Role Button */}
      <ReusableHeader
        title="Role"
        breadcrumbs={[{ label: 'Role Master' }]}
        actionButton={
          canCreate ? (
            <Button
              size="small"
              variant="contained"
              onClick={() => openDialog('new')}
              className="global-button global-button-primary"
              sx={{
                backgroundColor: 'var(--edge-blue)',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                borderRadius: '6px',
                px: 2.5,
                py: 0.75,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#1858BC',
                  boxShadow: 'none',
                },
              }}
            >
              Add Role
            </Button>
          ) : null
        }
      />

      <Box sx={{ mt: 3 }}>
        {/* Search Bar */}
        <TableFilterBar
          searchQuery={searchQuery}
          onSearchChange={(q) => {
            setSearchQuery(q);
            setPage(0);
          }}
          filters={[]}
          searchPlaceholder="Search Roles..."
        />

        {/* Roles Table */}
        <Paper
          className="global-card"
          sx={{
            width: '100%',
            overflow: 'hidden',
            borderRadius: '8px',
            border: '1px solid var(--hairline)',
            backgroundColor: 'var(--canvas)',
            boxShadow: 'none',
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 700 }} aria-label="Role Master Table">
              <TableHead
                sx={{
                  backgroundColor: 'var(--mist)',
                  borderBottom: '1px solid var(--hairline)',
                  '& th': {
                    color: 'var(--vault-navy)',
                    fontWeight: 700,
                    fontSize: '0.825rem',
                    py: 1.5,
                  },
                }}
              >
                <TableRow>
                  {/* Select All Checkbox */}
                  <TableCell padding="checkbox" sx={{ width: 48, pl: 2 }}>
                    <Checkbox
                      size="small"
                      indeterminate={
                        selectedRowIds.size > 0 && selectedRowIds.size < filteredRoles.length
                      }
                      checked={
                        filteredRoles.length > 0 && selectedRowIds.size === filteredRoles.length
                      }
                      onChange={handleSelectAllRows}
                      sx={{
                        color: 'var(--hairline)',
                        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                          color: 'var(--edge-blue)',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 50, fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ width: 220, fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ width: 180, fontWeight: 700 }}>Permissions</TableCell>
                  <TableCell sx={{ width: 200, fontWeight: 700 }}>Users</TableCell>
                  <TableCell sx={{ width: 140, fontWeight: 700 }}>Status</TableCell>
                  <TableCell align="right" sx={{ width: 80, pr: 2, fontWeight: 700 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} sx={{ color: 'var(--edge-blue)' }} />
                    </TableCell>
                  </TableRow>
                ) : paginatedRoles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" sx={{ color: 'var(--slate)', fontStyle: 'italic' }}>
                        No roles found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRoles.map((row, idx) => {
                    const rowGlobalIndex = page * rowsPerPage + idx + 1;
                    const isSelected = selectedRowIds.has(row._id);
                    const permCount = countAssignedPermissions(row);
                    const assignedUsers = usersByRoleId.get(String(row._id)) || [];

                    return (
                      <TableRow
                        key={row._id || idx}
                        hover
                        selected={isSelected}
                        sx={{
                          borderBottom: '1px solid var(--hairline)',
                          '&:last-child': { borderBottom: 'none' },
                          '&.Mui-selected': {
                            backgroundColor: 'var(--blue-tint) !important',
                          },
                          '&:hover': {
                            backgroundColor: 'var(--mist)',
                          },
                        }}
                      >
                        {/* Checkbox */}
                        <TableCell padding="checkbox" sx={{ pl: 2 }}>
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => handleToggleRow(row._id)}
                            sx={{
                              color: 'var(--hairline)',
                              '&.Mui-checked': { color: 'var(--edge-blue)' },
                            }}
                          />
                        </TableCell>

                        {/* # */}
                        <TableCell sx={{ color: 'var(--slate)', fontSize: '0.85rem' }}>
                          {rowGlobalIndex}
                        </TableCell>

                        {/* Role Name */}
                        <TableCell sx={{ color: 'var(--vault-navy)', fontWeight: 700, fontSize: '0.9rem' }}>
                          {row.role}
                        </TableCell>

                        {/* Permissions Count */}
                        <TableCell sx={{ color: 'var(--vault-navy)', fontSize: '0.85rem' }}>
                          {permCount} assigned
                        </TableCell>

                        {/* Users Avatars */}
                        <TableCell>
                          {assignedUsers.length === 0 ? (
                            <Typography variant="body2" sx={{ color: 'var(--slate)' }}>
                              —
                            </Typography>
                          ) : (
                            <Stack direction="row" spacing={0.6} alignItems="center">
                              {assignedUsers.slice(0, 3).map((u, userIdx) => {
                                const initial = (
                                  u.first_name?.[0] ||
                                  u.username?.[0] ||
                                  u.email?.[0] ||
                                  'U'
                                ).toUpperCase();
                                const palette =
                                  AVATAR_PALETTES[userIdx % AVATAR_PALETTES.length];
                                return (
                                  <Box
                                    key={u._id || userIdx}
                                    title={
                                      u.first_name
                                        ? `${u.first_name} ${u.last_name || ''}`
                                        : u.username || u.email
                                    }
                                    sx={{
                                      width: 26,
                                      height: 26,
                                      borderRadius: '50%',
                                      backgroundColor: palette.bg,
                                      color: palette.color,
                                      fontSize: '0.75rem',
                                      fontWeight: 700,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'default',
                                    }}
                                  >
                                    {initial}
                                  </Box>
                                );
                              })}
                              {assignedUsers.length > 3 && (
                                <Box
                                  sx={{
                                    height: 22,
                                    px: 0.8,
                                    borderRadius: '11px',
                                    backgroundColor: 'var(--mist)',
                                    border: '1px solid var(--hairline)',
                                    color: 'var(--vault-navy)',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  +{assignedUsers.length - 3}
                                </Box>
                              )}
                            </Stack>
                          )}
                        </TableCell>

                        {/* Status Badge */}
                        <TableCell>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              px: 1.25,
                              py: 0.35,
                              borderRadius: '16px',
                              backgroundColor:
                                row.status === 'Inactive'
                                  ? 'var(--mist)'
                                  : 'rgba(0, 168, 143, 0.12)',
                              color:
                                row.status === 'Inactive'
                                  ? 'var(--slate)'
                                  : 'var(--secure)',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              border: `1px solid ${
                                row.status === 'Inactive'
                                  ? 'var(--hairline)'
                                  : 'rgba(0, 168, 143, 0.3)'
                              }`,
                            }}
                          >
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor:
                                  row.status === 'Inactive'
                                    ? 'var(--slate)'
                                    : 'var(--secure)',
                              }}
                            />
                            {row.status === 'Inactive' ? 'Inactive' : 'Active'}
                          </Box>
                        </TableCell>

                        {/* Actions (3-Dots Menu) */}
                        <TableCell align="right" sx={{ pr: 2 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                            sx={{
                              color: 'var(--slate)',
                              '&:hover': {
                                color: 'var(--vault-navy)',
                                backgroundColor: 'var(--mist)',
                              },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Table Pagination Controls */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: 3,
              py: 1.5,
              borderTop: '1px solid var(--hairline)',
              gap: 3,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ color: 'var(--slate)', fontSize: '0.85rem' }}>
                Rows per page:
              </Typography>
              <Select
                value={rowsPerPage}
                size="small"
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                sx={{
                  fontSize: '0.85rem',
                  color: 'var(--vault-navy)',
                  height: 32,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--hairline)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--edge-blue)',
                  },
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </Stack>

            <Typography variant="body2" sx={{ color: 'var(--slate)', fontSize: '0.85rem' }}>
              {totalRoles === 0 ? '0–0 of 0' : `${startItem}–${endItem} of ${totalRoles}`}
            </Typography>

            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                sx={{
                  color: 'var(--vault-navy)',
                  '&.Mui-disabled': { color: 'var(--hairline)' },
                }}
              >
                <KeyboardArrowLeftIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                sx={{
                  color: 'var(--vault-navy)',
                  '&.Mui-disabled': { color: 'var(--hairline)' },
                }}
              >
                <KeyboardArrowRightIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </Paper>
      </Box>

      {/* Row Action Dropdown Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          style: V,
          sx: {
            borderRadius: '8px',
            border: '1px solid var(--hairline)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            minWidth: 130,
            py: 0.5,
            backgroundColor: 'var(--canvas)',
          },
        }}
      >
        {canUpdate && (
          <MenuItem
            onClick={() => {
              const rec = menuRecord;
              handleCloseMenu();
              openDialog('edit', rec);
            }}
            sx={{
              fontSize: '0.85rem',
              color: 'var(--vault-navy)',
              gap: 1.5,
              py: 1,
              '&:hover': { backgroundColor: 'var(--mist)' },
            }}
          >
            <ModeEditOutlineIcon fontSize="small" sx={{ color: 'var(--slate)' }} />
            Edit
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={() => handleDeleteClick(menuRecord)}
            sx={{
              fontSize: '0.85rem',
              color: 'var(--critical)',
              gap: 1.5,
              py: 1,
              '&:hover': { backgroundColor: 'var(--mist)' },
            }}
          >
            <DeleteOutlineIcon fontSize="small" sx={{ color: 'var(--critical)' }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* =========================================================
         Create / Edit Role Modal
         ========================================================= */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          style: V,
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
            maxWidth: 880,
            backgroundColor: 'var(--canvas)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          },
        }}
      >
        {/* Modal Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            borderBottom: '1px solid var(--hairline)',
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: 'var(--vault-navy)', fontSize: '1.2rem', lineHeight: 1.3 }}
            >
              {currentAction === 'edit' ? 'Edit role' : 'Create role'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--slate)', fontSize: '0.85rem', mt: 0.25 }}>
              Name the role, then choose what it can do.
            </Typography>
          </Box>
          <IconButton
            onClick={closeDialog}
            sx={{
              color: 'var(--slate)',
              p: 0.5,
              border: '1px solid var(--hairline)',
              borderRadius: '6px',
              '&:hover': { backgroundColor: 'var(--mist)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Modal Body: Two Columns */}
        <DialogContent sx={{ p: 3, backgroundColor: 'var(--canvas)' }}>
          <Grid container spacing={3}>
            {/* Left Column: Role Details */}
            <Grid item xs={12} md={5}>
              {/* Role Name */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'var(--vault-navy)', mb: 0.75, fontSize: '0.85rem' }}
                >
                  Role name <span style={{ color: 'var(--critical)' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter role name"
                  value={roleName}
                  onChange={(e) => {
                    setRoleName(e.target.value);
                    if (roleError) setRoleError('');
                  }}
                  error={Boolean(roleError)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ShieldOutlinedIcon sx={{ color: 'var(--slate)', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: '6px',
                      backgroundColor: 'var(--canvas)',
                      fontSize: '0.875rem',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: roleError ? 'var(--critical)' : 'var(--hairline)',
                      },
                    },
                  }}
                />
                {roleError && (
                  <Typography variant="caption" sx={{ color: 'var(--critical)', mt: 0.5, display: 'block' }}>
                    {roleError}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{ color: 'var(--slate)', mt: 0.5, display: 'block', fontSize: '0.75rem' }}
                >
                  Shown in the role list and when assigning users.
                </Typography>
              </Box>

              {/* Description */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'var(--vault-navy)', mb: 0.75, fontSize: '0.85rem' }}
                >
                  Description
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="What this role is for."
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  InputProps={{
                    sx: {
                      borderRadius: '6px',
                      backgroundColor: 'var(--canvas)',
                      fontSize: '0.875rem',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--hairline)',
                      },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: 'var(--slate)', mt: 0.5, display: 'block', fontSize: '0.75rem' }}
                >
                  What this role is for.
                </Typography>
              </Box>

              {/* Status */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'var(--vault-navy)', mb: 0.75, fontSize: '0.85rem' }}
                >
                  Status
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  <Button
                    size="small"
                    variant={roleStatus === 'Active' ? 'contained' : 'outlined'}
                    onClick={() => setRoleStatus('Active')}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '6px',
                      px: 2.5,
                      py: 0.6,
                      fontWeight: 600,
                      fontSize: '0.825rem',
                      boxShadow: 'none',
                      backgroundColor: roleStatus === 'Active' ? '#00A88F' : 'transparent',
                      borderColor: roleStatus === 'Active' ? '#00A88F' : 'var(--hairline)',
                      color: roleStatus === 'Active' ? '#fff' : 'var(--slate)',
                      '&:hover': {
                        boxShadow: 'none',
                        backgroundColor: roleStatus === 'Active' ? '#00907A' : 'var(--mist)',
                        borderColor: roleStatus === 'Active' ? '#00907A' : 'var(--hairline)',
                      },
                    }}
                  >
                    Active
                  </Button>
                  <Button
                    size="small"
                    variant={roleStatus === 'Inactive' ? 'contained' : 'outlined'}
                    onClick={() => setRoleStatus('Inactive')}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '6px',
                      px: 2.5,
                      py: 0.6,
                      fontWeight: 600,
                      fontSize: '0.825rem',
                      boxShadow: 'none',
                      backgroundColor: roleStatus === 'Inactive' ? '#6B7A90' : 'transparent',
                      borderColor: roleStatus === 'Inactive' ? '#6B7A90' : 'var(--hairline)',
                      color: roleStatus === 'Inactive' ? '#fff' : 'var(--slate)',
                      '&:hover': {
                        boxShadow: 'none',
                        backgroundColor: roleStatus === 'Inactive' ? '#5A677A' : 'var(--mist)',
                        borderColor: roleStatus === 'Inactive' ? '#5A677A' : 'var(--hairline)',
                      },
                    }}
                  >
                    Inactive
                  </Button>
                </Stack>
                <Typography
                  variant="caption"
                  sx={{ color: 'var(--slate)', mt: 0.75, display: 'block', fontSize: '0.75rem' }}
                >
                  Inactive roles can't be assigned to new users.
                </Typography>
              </Box>

              {/* Date Permission Policy */}
              <Box sx={{ mt: 2, p: 1.5, borderRadius: '6px', backgroundColor: 'var(--mist)', border: '1px solid var(--hairline)' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={datePermissionChecked}
                      onChange={(e) => setDatePermissionChecked(e.target.checked)}
                      sx={{
                        color: 'var(--hairline)',
                        '&.Mui-checked': { color: 'var(--edge-blue)' },
                        p: 0.5,
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.825rem', color: 'var(--vault-navy)' }}>
                      Date Permission
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
                <Typography variant="caption" sx={{ color: 'var(--slate)', display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                  Enable time-bound / date-restricted permission policy for this role.
                </Typography>
              </Box>
            </Grid>

            {/* Right Column: Grouped Permissions */}
            <Grid item xs={12} md={7}>
              {/* Permissions Header & Counter Badge */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <Typography sx={{ fontWeight: 700, color: 'var(--vault-navy)', fontSize: '0.95rem' }}>
                  Permissions
                </Typography>
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.35,
                    borderRadius: '12px',
                    backgroundColor: 'var(--blue-tint)',
                    color: 'var(--edge-blue)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  {selectedPermIds.size} of {totalCatalogPermissionsCount} selected
                </Box>
              </Stack>

              <Typography
                variant="caption"
                sx={{ color: 'var(--slate)', display: 'block', mb: 2, fontSize: '0.75rem', lineHeight: 1.4 }}
              >
                Each feature has its own actions. Create, edit and similar actions need the feature's View
                permission, so it's selected for you.
              </Typography>

              {/* Scrollable Container for Group Cards */}
              <Box
                sx={{
                  maxHeight: 450,
                  overflowY: 'auto',
                  pr: 0.5,
                  '&::-webkit-scrollbar': { width: 5 },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'var(--hairline)',
                    borderRadius: 3,
                  },
                }}
              >
                {permissionGroups.map((group) => {
                  const selectedCount = getGroupSelectedCount(group);
                  const totalCount = getGroupTotalCount(group);
                  const isExpanded = Boolean(expandedGroups[group.id]);

                  return (
                    <Box
                      key={group.id}
                      sx={{
                        mb: 1.5,
                        border: '1px solid var(--hairline)',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'var(--canvas)',
                      }}
                    >
                      {/* Group Header */}
                      <Box
                        onClick={() => toggleGroupExpand(group.id)}
                        sx={{
                          px: 2,
                          py: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          backgroundColor: 'var(--mist)',
                          borderBottom: isExpanded ? '1px solid var(--hairline)' : 'none',
                          userSelect: 'none',
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--vault-navy)' }}>
                          {group.title}
                        </Typography>

                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Typography variant="caption" sx={{ color: 'var(--slate)', fontSize: '0.75rem' }}>
                            {selectedCount} of {totalCount}
                          </Typography>
                          <Typography
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSelectAllGroup(group);
                            }}
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              color: 'var(--edge-blue)',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            Select all
                          </Typography>
                          <IconButton size="small" sx={{ p: 0.25, color: 'var(--slate)' }}>
                            {isExpanded ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Stack>
                      </Box>

                      {/* Collapsible Group Content */}
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>{renderGroupContent(group)}</Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        {/* Modal Footer */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid var(--hairline)',
            backgroundColor: 'var(--canvas)',
            gap: 1,
          }}
        >
          <Button
            onClick={closeDialog}
            sx={{
              textTransform: 'none',
              color: 'var(--slate)',
              fontWeight: 600,
              fontSize: '0.85rem',
              '&:hover': { backgroundColor: 'var(--mist)' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={saving}
            sx={{
              textTransform: 'none',
              backgroundColor: 'var(--edge-blue)',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.85rem',
              borderRadius: '6px',
              px: 3,
              py: 0.75,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#1858BC',
                boxShadow: 'none',
              },
            }}
          >
            {saving ? 'Saving...' : currentAction === 'edit' ? 'Save changes' : 'Create role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={handleCancelDelete}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          style: V,
          sx: {
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: 'var(--canvas)',
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 3, py: 2, borderBottom: '1px solid var(--hairline)' }}
        >
          <Typography variant="h6" sx={{ color: 'var(--vault-navy)', fontWeight: 700, fontSize: '1.05rem' }}>
            Delete Role
          </Typography>
          <IconButton onClick={handleCancelDelete} sx={{ color: 'var(--slate)', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Typography sx={{ color: 'var(--vault-navy)', fontSize: '0.925rem' }}>
            Are you sure you want to delete the role{' '}
            <strong style={{ color: 'var(--vault-navy)' }}>
              {deletingRole ? deletingRole.role : ''}
            </strong>
            ?
          </Typography>
          <Typography sx={{ mt: 1, color: 'var(--slate)', fontSize: '0.825rem' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid var(--hairline)',
            backgroundColor: 'var(--mist)',
          }}
        >
          <Button
            onClick={handleCancelDelete}
            disabled={deleting}
            sx={{
              textTransform: 'none',
              color: 'var(--slate)',
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            sx={{
              textTransform: 'none',
              backgroundColor: 'var(--critical)',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.85rem',
              borderRadius: '6px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#B52830',
                boxShadow: 'none',
              },
            }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Feedback */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackClose}
          severity={snackSeverity}
          style={V}
          sx={{
            width: '100%',
            backgroundColor: 'var(--canvas)',
            color: 'var(--vault-navy)',
            border: '1px solid var(--hairline)',
          }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Role;